import dayjs from 'dayjs';
import { date, number, object, string } from 'yup';

import { MUST_BE_GREATER_THAN_ZERO, MUST_BE_INTEGER, REQUIRED_FIELD } from '@/config/text.const';
import { GetBillingIntervalTypes } from '@/graphql/contracts/_generated_/getBillingIntervalTypes.query';
import { GetBillingTypes } from '@/graphql/contracts/_generated_/getBillingTypes.query';
import { GetUnitPriceFrequencies } from '@/graphql/contracts/_generated_/getUnitPriceFrequencies.query';
import { SearchProducts } from '@/graphql/contracts/_generated_/searchProducts.query';
import { EnvUtils } from '@/utils/env.utils';

export const validationSchema = object().shape({
  stockCode: string()
    .transform((currentValue: SearchProducts['search'][number]) => {
      return currentValue?.stockCode || currentValue;
    })
    .typeError(REQUIRED_FIELD)
    .required(REQUIRED_FIELD),

  quantity: number()
    .typeError('Must be a number')
    .min(1, MUST_BE_GREATER_THAN_ZERO)
    .integer(MUST_BE_INTEGER)
    .required(REQUIRED_FIELD),

  unitPrice: number()
    .typeError('Must be a number')
    .required(REQUIRED_FIELD),

  comments: string()
    .max(200, 'Must be maximum ${max} characters')
    .nullable(),

  billingStartDate: date()
    .test((value, context) => {
      const contractStartDate = context.options.context?.contract?.contractStartDate;

      if (!contractStartDate) {
        return context.createError({
          path: 'billingStartDate',
          message: 'Can\'t get "Contract Start Date"'
        });
      }

      if (value && !dayjs(value).isSameOrAfter(dayjs(contractStartDate), 'date')) {
        return context.createError({
          path: 'billingStartDate',
          message: 'Billing Start Date can\'t be less than Contract Start Date'
        });
      }

      return true;
    })
    .required(REQUIRED_FIELD)
    .nullable(),

  ...(!EnvUtils.isProRataFirstPeriodEnabled() ? {
    lineStartDate: date()
      .test((value, context) => {
        const contractStartDate = context.options.context?.contract?.contractStartDate;

        if (!contractStartDate) {
          return context.createError({
            path: 'lineStartDate',
            message: 'Can\'t get "Contract Start Date"'
          });
        }

        if (value && !dayjs(value).isSameOrAfter(dayjs(contractStartDate), 'date')) {
          return context.createError({
            path: 'lineStartDate',
            message: 'Line Start Date can\'t be less than Contract Start Date'
          });
        }

        return true;
      })
      .required(REQUIRED_FIELD)
      .nullable(),
  } : {}),

  billingType: string()
    .transform((currentValue: GetBillingTypes['billingTypes'][number]) => {
      return (currentValue && currentValue?.type)
        ? currentValue?.type
        : currentValue;
    })
    .required(REQUIRED_FIELD),

  billingInterval: string()
    .transform((currentValue: GetBillingIntervalTypes['billingIntervalTypes'][number]) => {
      return (currentValue && currentValue?.billingInterval)
        ? currentValue?.billingInterval
        : currentValue;
    })
    .when('billingType', {
      is: (billingType: any) =>
        (typeof billingType === 'string' && billingType === 'Subscription') ||
        (typeof billingType === 'object' && billingType?.type === 'Subscription'),
      then: (schema) => schema.required('Billing Interval is required for Billing Type "Subscription"'),
      otherwise: (schema) => schema.nullable(),
    })
    .nullable(),

  unitPriceFrequency: string()
    .transform((currentValue: GetUnitPriceFrequencies['unitPriceFrequencies'][number]) => {
      return currentValue && currentValue?.frequency
        ? currentValue?.frequency
        : currentValue;
    })
    .when('billingType', {
      is: (billingType: any) =>
        (typeof billingType === 'string' && billingType === 'Subscription') ||
        (typeof billingType === 'object' && billingType?.type === 'Subscription'),
      then: (schema) => schema.required('Unit Price Frequency is required for Billing Type "Subscription"'),
      otherwise: (schema) => schema.nullable(),
    })
    .nullable(),
  shipToCustomer: object().shape({
    customerId: string()
      .nullable()
  }).nullable(),
  invoiceToCustomer: object().shape({
    customerId: string()
      .nullable()
  }).nullable(),
  unitType: string()
    .max(10, 'Must be maximum ${max} characters')
    .nullable(),
  poNumber: string()
    .nullable(),
  ...(EnvUtils.isProRataFirstPeriodEnabled() ? {
    proRataEndDate: date()
      .test((value, context) => {
        const billingStartDate = context.parent?.billingStartDate;

        if (!value) {
          return true;
        }

        if (!billingStartDate) {
          return context.createError({
            path: 'proRataEndDate',
            message: 'Billing Start Date is required when Pro Rata End Date is specified'
          });
        }

        if (!dayjs(value).isAfter(dayjs(billingStartDate), 'date')) {
          return context.createError({
            path: 'proRataEndDate',
            message: 'Pro Rata End Date must be after Billing Start Date'
          });
        }

        return true;
      })
      .nullable(),
  } : {}),
});
